<template>
  <section class="banner-section">
    <div class="container">
      <!-- 로딩 상태 -->
      <div v-if="isLoading" class="loading-box"></div>

      <!-- 배너 없음 -->
      <div v-else-if="banners.length === 0" class="empty-box">
        등록된 배너가 없습니다.
      </div>

      <!-- 배너 슬라이더 -->
      <div v-else class="banner-wrapper" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay">
        <div
          :class="['banner-track', { transitioning: isTransitioning }]"
          :style="{ transform: `translateX(${translateX}px)` }"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div
            v-for="(banner, index) in infiniteBanners"
            :key="`banner-${index}`"
            class="banner-item"
            ref="bannerItems"
          >
            <div
              class="banner-card"
              @click="handleBannerClick(banner)"
              :style="{ cursor: banner.linkUrl ? 'pointer' : 'default' }"
            >
              <img :src="banner.imageUrl" alt="Event Banner" />
            </div>
          </div>
        </div>

        <!-- 좌우 화살표 (데스크탑) -->
        <button class="arrow arrow-left" @click="prevSlide">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18L9 12L15 6"/>
          </svg>
        </button>
        <button class="arrow arrow-right" @click="nextSlide">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18L15 12L9 6"/>
          </svg>
        </button>
      </div>

      <!-- 인디케이터 -->
      <div v-if="!isLoading && banners.length > 0" class="indicators">
        <button
          v-for="(_, index) in banners"
          :key="`indicator-${index}`"
          :class="['indicator', { active: index === currentRealIndex }]"
          @click="goToSlide(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useMainBanner } from '@/composables/main/useMainBanner'
import { useRouter } from 'vue-router'

export default {
  name: 'BannerSection',
  setup() {
    const router = useRouter()
    const { getActiveBanners } = useMainBanner()

    const banners = ref([])
    const currentIndex = ref(0)
    const translateX = ref(0)
    const itemWidth = ref(0)
    const autoPlayInterval = ref(null)
    const isTransitioning = ref(false)
    const isLoading = ref(true)
    const touchStartX = ref(0)
    const touchEndX = ref(0)
    const isPaused = ref(false)
    const bannerItems = ref([])

    // 무한 롤링을 위한 배너 배열
    const infiniteBanners = computed(() => {
      if (banners.value.length === 0) return []
      return [banners.value[banners.value.length - 1], ...banners.value, banners.value[0]]
    })

    // 실제 인덱스 (인디케이터용)
    const currentRealIndex = computed(() => {
      if (banners.value.length === 0) return 0
      if (currentIndex.value === 0) return banners.value.length - 1
      if (currentIndex.value === infiniteBanners.value.length - 1) return 0
      return currentIndex.value - 1
    })

    // 배너 데이터 로드
    const fetchBanners = async () => {
      try {
        const data = await getActiveBanners()
        banners.value = data

        setTimeout(() => {
          isLoading.value = false

          if (banners.value.length > 0) {
            updateItemWidth()
            currentIndex.value = 1
            updateTranslateX(false)
            startAutoPlay()
          }
        }, 500)
      } catch (error) {
        console.error('배너 로딩 실패:', error)
        isLoading.value = false
      }
    }

    // 배너 클릭 핸들러
    const handleBannerClick = (banner) => {
      if (!banner.linkUrl) return

      if (banner.linkUrl.startsWith('http')) {
        window.open(banner.linkUrl, '_blank')
      } else {
        // 캠페인 랜딩 페이지로 이동 시 ?from=banner 추가
        const separator = banner.linkUrl.includes('?') ? '&' : '?'
        router.push(`${banner.linkUrl}${separator}from=banner`)
      }
    }

    // 아이템 너비 업데이트
    const updateItemWidth = () => {
      if (bannerItems.value?.[0]) {
        itemWidth.value = bannerItems.value[0].offsetWidth
      }
    }

    // translateX 업데이트
    const updateTranslateX = (animate) => {
      isTransitioning.value = animate
      translateX.value = -currentIndex.value * itemWidth.value
    }

    // 다음 슬라이드
    const nextSlide = () => {
      if (isTransitioning.value) return
      isTransitioning.value = true
      currentIndex.value++
      updateTranslateX(true)

      setTimeout(() => {
        if (currentIndex.value >= infiniteBanners.value.length - 1) {
          isTransitioning.value = false
          currentIndex.value = 1
          updateTranslateX(false)
        }
        isTransitioning.value = false
      }, 400)

      resetAutoPlay()
    }

    // 이전 슬라이드
    const prevSlide = () => {
      if (isTransitioning.value) return
      isTransitioning.value = true
      currentIndex.value--
      updateTranslateX(true)

      setTimeout(() => {
        if (currentIndex.value <= 0) {
          isTransitioning.value = false
          currentIndex.value = infiniteBanners.value.length - 2
          updateTranslateX(false)
        }
        isTransitioning.value = false
      }, 400)

      resetAutoPlay()
    }

    // 특정 슬라이드로 이동
    const goToSlide = (index) => {
      currentIndex.value = index + 1
      updateTranslateX(true)
      resetAutoPlay()
    }

    // 자동 재생
    const startAutoPlay = () => {
      autoPlayInterval.value = setInterval(() => {
        if (!isPaused.value) nextSlide()
      }, 4000)
    }

    const stopAutoPlay = () => {
      clearInterval(autoPlayInterval.value)
    }

    const resetAutoPlay = () => {
      stopAutoPlay()
      startAutoPlay()
    }

    const pauseAutoPlay = () => {
      isPaused.value = true
    }

    const resumeAutoPlay = () => {
      isPaused.value = false
    }

    // 터치 이벤트
    const handleTouchStart = (e) => {
      touchStartX.value = e.touches[0].clientX
    }

    const handleTouchMove = (e) => {
      touchEndX.value = e.touches[0].clientX
    }

    const handleTouchEnd = () => {
      const diff = touchStartX.value - touchEndX.value
      if (Math.abs(diff) > 50) {
        diff > 0 ? nextSlide() : prevSlide()
      }
    }

    // 리사이즈 핸들러
    const handleResize = () => {
      updateItemWidth()
      updateTranslateX(false)
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize)
      fetchBanners()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
      stopAutoPlay()
    })

    return {
      banners,
      infiniteBanners,
      currentRealIndex,
      translateX,
      isTransitioning,
      isLoading,
      bannerItems,
      handleBannerClick,
      nextSlide,
      prevSlide,
      goToSlide,
      pauseAutoPlay,
      resumeAutoPlay,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.banner-section {
  @apply w-full bg-white;
  padding: 40px 0 16px;
}

.container {
  @apply mx-auto relative;
  max-width: 1120px;
  padding: 0 40px;
}

.loading-box {
  @apply bg-slate-100 rounded-2xl;
  height: 300px;
  animation: pulse 2s infinite;
}

.empty-box {
  @apply flex items-center justify-center text-slate-400 bg-slate-50 rounded-2xl;
  height: 200px;
}

.banner-wrapper {
  @apply relative overflow-hidden;
}

.banner-track {
  @apply flex;
  transition: none;
}

.banner-track.transitioning {
  transition: transform 0.4s ease;
}

.banner-item {
  @apply flex-shrink-0 box-border;
  flex: 0 0 100%;
  padding: 0 8px;
}

.banner-card {
  @apply w-full rounded-2xl overflow-hidden relative;
  aspect-ratio: 2/1;
}

.banner-card img {
  @apply w-full h-full object-cover block;
}

/* 화살표 */
.arrow {
  @apply absolute hidden items-center justify-center border-none cursor-pointer;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  z-index: 10;
}

.arrow:hover {
  @apply text-white;
  background: #3b82f6;
}

.arrow-left {
  left: 16px;
}

.arrow-right {
  right: 16px;
}

/* 인디케이터 */
.indicators {
  @apply flex justify-center;
  gap: 8px;
  margin-top: 24px;
}

.indicator {
  @apply border-none cursor-pointer p-0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d1d5db;
  transition: all 0.3s;
}

.indicator.active {
  background: #3b82f6;
  width: 24px;
  border-radius: 4px;
}

/* 데스크탑에서 2개씩 표시 + 화살표 보이기 */
@media (min-width: 768px) {
  .banner-item {
    flex: 0 0 50%;
  }

  .arrow {
    @apply flex;
  }
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
</style>
